import { isSameDay } from 'date-fns';

import { parseApiDateString } from '@/utils';

export function checkRationalesLocked(store, flashcardCategory, apptimize) {
  // Ratioanles should never be locked when onTour
  if (store.getters['session/onTour']) {
    return false;
  }

  // If Apptimize is initialized, check for the 'rationalesLocked' flag
  // If there the flag has a value, stop here. Otherwise, move on
  // If the user has paid, override the Apptimize flag and unlock all rationales
  // Otherwise, return the value from the Apptimize bool
  if (apptimize.enabled()) {
    const apptimizeBool = apptimize.root.getBool('rationalesLocked', null);

    if (apptimizeBool !== null) {
      if (store.getters['session/hasPaid']) {
        return false;
      }
      return apptimizeBool;
    }
  }

  // Finally, check the category to determine if it's a lockable category
  // If the flashcard category type is multiple choice, quiz, or userQuiz, check the POs for Paid Rationales
  if (flashcardCategory) {
    const userQuizCatType = store.getters['categoryTypes/userQuizCategoryType'];
    const multipleChoiceCatType = store.getters['categoryTypes/multipleChoiceCategoryTypes'];
    const quizCatTypes = store.getters['categoryTypes/quizCategoryTypes'];

    const gatedCategoryIds = [
      ...quizCatTypes,
      ...multipleChoiceCatType,
      userQuizCatType,
    ].filter(ct => ct).map(ct => ct.id);

    if (gatedCategoryIds.includes(flashcardCategory.categoryTypeId)) {
      const userPOIds = store.getters['session/currentUser'].purchaseOrders;
      const userPOList = store.getters['purchaseOrders/all'].filter(po => userPOIds.includes(po.id));
      const hasPaidRationales = userPOList.some(po => po.features && po.features.includes('Paid Rationales'));

      return !hasPaidRationales;
    }
  }

  // If none of these conditions are true, rationales are not locked
  return false;
}

export function hasAdvancedStats(store, apptimize, analytics) {
  if (store.getters['session/onTour']) {
    return false;
  }

  // disabling this because PO flags should take precedent now and the experiments aren't needed
  // if (apptimize?.enabled()) {
  //   const advancedStats = apptimize.checkExperiments({
  //     experiment: 'Advanced Flashcard Stats',
  //     bool: 'advancedFlashcardStats',
  //     defaultVal: null,
  //     matcher: /advanced flashcard stats/gi,
  //     analytics: analytics,
  //   });

  //   return advancedStats;
  // }

  // Empty array needed for marketing flashcards
  // TODO: Allow marketing pages to function without this empty array
  const userPOIds = store.getters['session/currentUser'] ? store.getters['session/currentUser'].purchaseOrders : [];
  const userPOList = store.getters['purchaseOrders/all'].filter(po => userPOIds.includes(po.id));
  return userPOList.some(po => po.features && po.features.includes('Advanced Flashcard Stats'));
}

export function hasAnsweredFlashcard(flashcard, answerIds, rawAnswerData, terminology = false) {
  if (flashcard.type === 'Hotspot') {
    return !(rawAnswerData || {}).x || !(rawAnswerData || {}).y;
  } else if (flashcard.type === 'Blank Text Completion') {
    return answerIds.length !== (flashcard.answers.length / flashcard.blankTextColumns);
  } else if (flashcard.type === 'Ordered Response') {
    return false;
  } else if (flashcard.type === 'Numeric Entry') {
    return !(rawAnswerData || {}).numeric;
  } else if (flashcard.type === 'Fractional Numeric Entry') {
    return !((rawAnswerData || {}).numerator && (rawAnswerData || {}).denominator);
  } else if (flashcard.type === 'Matrix Grid') {
    // Matrix Grid flashcards are considered answered if every choice in the
    // matrixGridSelections array has at least one true value
    // For some reason we need to return the opposite of this despite the name of this function...
    // TODO: Fix the naming of this function and make it much more readable
    const everyChoiceHasATrue = (rawAnswerData || {}).matrixGridSelections?.every(choice => choice.selections.some(value => value === true));
    return !everyChoiceHasATrue;
  } else if (flashcard.type === 'Bowtie') {
    return answerIds.length < 5;
  } else {
    return answerIds.length < 1 && !terminology;
  }
}

export function flashcardType(flashcard) {
  if (flashcard.type === 'Flipcard') {
    return 'terminology';
  } else if (flashcard.type === 'SATA') {
    return 'multiple-choice';
  } else if (flashcard.type === 'Cloze / Dropdown') {
    return 'cloze-dropdown';
  } else if (flashcard.type) {
    return flashcard.type.toLowerCase().split(' ').join('-');
  }
}

export function flashcardStatsByDay(stats, day = new Date()) {
  const dayStats = stats.flashcard?.find(stat => {
    const statDate = parseApiDateString(stat.date).date;
    return isSameDay(statDate, day);
  });
  return dayStats?.count || 0;
}

export function checkMatrixGridAnswerCorrectness(flashcard, matrixGridSelections, answerId) {
  const userAnswer = matrixGridSelections?.find(answerObj => answerObj.answerId === answerId)?.selections || [];
  const flashcardAnswer = flashcard.answers.find(answer => answer.id === answerId)?.matrixGridCorrect || [];
  return userAnswer.every((answer, index) => answer === flashcardAnswer[index]);
}

export function checkMatrixGridAnswerAnswered(matrixGridSelections, answerId) {
  // This determines if a given answer has any true values indicating that a user has answered it
  return matrixGridSelections?.find(answerObj => answerObj.answerId === answerId)?.selections?.some(answer => answer);
}

export function checkMatrixGridSelectionCorrectness(flashcard, matrixGridSelections, answerId, selectionIndex) {
  const userAnswer = getMatrixGridUserAnswer(matrixGridSelections, answerId, selectionIndex);
  const flashcardAnswer = getMatrixGridFlashcardAnswer(flashcard, answerId, selectionIndex);
  if (flashcardAnswer === true) {
    return 'correct';
  } else if (userAnswer === true && flashcardAnswer === false) {
    return 'incorrect';
  }
}

export function checkMatrixGridSelectionSelected(matrixGridSelections, answerId, selectionIndex) {
  const userAnswer = getMatrixGridUserAnswer(matrixGridSelections, answerId, selectionIndex);
  return userAnswer === true;
}

export function getMatrixGridUserAnswer(matrixGridSelections, answerId, selectionIndex) {
  return matrixGridSelections?.find(answerObj => answerObj.answerId === answerId)?.selections[selectionIndex];
}

export function getMatrixGridFlashcardAnswer(flashcard, answerId, selectionIndex) {
  return flashcard?.answers?.find(answer => answer.id === answerId)?.matrixGridCorrect[selectionIndex];
}
